<template>
  <div v-if="loaded && enabledModulesCount > 1">
    <!-- Navegação com campo de busca de protocolo -->
    <div class="module-navigation">
      <div class="nav-buttons-container">
        <!-- Campo de busca protocolo -->
        <div class="protocol-search-wrapper">
          <div class="search-container">
            <div class="search-input-wrapper">
              <input
                id="protocol-search"
                v-model="protocol"
                class="search-input"
                placeholder="Digite o protocolo"
                @keyup.enter="tryOpenProtocol"
              />
              <button
                class="search-button"
                type="button"
                @click="tryOpenProtocol"
              >
                <i class="fas fa-search" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Filtro de Empresas (para company-root gestora, apenas no dashboard geral) -->
        <div v-if="showCompanyFilter" class="company-filter-wrapper">
          <searchable-select
            id="company-filter"
            v-model="selectedCompanyId"
            :options="companyOptions"
            :placeholder="'Selecione a Empresa'"
            :search-placeholder="'Buscar empresa...'"
            class="company-filter-select"
          />
        </div>

        <!-- Botões -->
        <div class="buttons-group">
          <button
            v-if="hasPermission('complaint')"
            class="nav-button"
            :class="{ active: activeModule === 'complaint' }"
            @click="setActiveModule('complaint')"
          >
            Gestão de Relatos
          </button>
          <button
            v-if="hasPermission('conformity')"
            class="nav-button"
            :class="{ active: activeModule === 'conformity' }"
            @click="setActiveModule('conformity')"
          >
            Gestão de Conformidade
          </button>
          <button
            v-if="hasPermission('dsar')"
            class="nav-button"
            :class="{ active: activeModule === 'dsar' }"
            @click="setActiveModule('dsar')"
          >
            LGPD
          </button>
          <button
            v-if="hasPermission('ombudsman')"
            class="nav-button"
            :class="{ active: activeModule === 'ombudsman' }"
            @click="setActiveModule('ombudsman')"
          >
            Ouvidoria
          </button>
          <button
            v-if="hasPermission('nr1')"
            class="nav-button"
            :class="{ active: activeModule === 'nr1' }"
            @click="setActiveModule('nr1')"
          >
            NR-1
          </button>
          <button
            v-if="hasPermission('grc')"
            class="nav-button"
            :class="{ active: activeModule === 'grc' }"
            @click="setActiveModule('grc')"
          >
            GRC
          </button>
          <!--<button class="ask-ai-button" @click="askAI">
            <i class="fas fa-magic" aria-hidden="true"></i>
            <span>Ask AI!</span>
          </button>-->
        </div>
      </div>
    </div>

    <!-- Conteúdo dos Módulos -->
    <div class="module-content">
      <complaint-dashboard
        v-if="activeModule === 'complaint' && hasPermission('complaint')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
      ></complaint-dashboard>
      <conformity-dashboard
        v-if="activeModule === 'conformity' && hasPermission('conformity')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
      ></conformity-dashboard>
      <dsar-dashboard
        v-if="activeModule === 'dsar' && hasPermission('dsar')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
      ></dsar-dashboard>
      <ombudsman-dashboard
        v-if="activeModule === 'ombudsman' && hasPermission('ombudsman')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
      ></ombudsman-dashboard>
      <nr1-dashboard
        v-if="activeModule === 'nr1' && hasPermission('nr1')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
      ></nr1-dashboard>
      <grc-dashboard
        v-if="activeModule === 'grc' && hasPermission('grc')"
        :company="originalCompany"
        :filter-company-id="effectiveFilterCompanyId"
        :allow-aggregate="showCompanyFilter"
      ></grc-dashboard>
    </div>
  </div>
  <div
    v-else-if="loaded && enabledModulesCount === 1"
    class="container-fluid mt-4"
  >
    <component
      :is="singleModuleDashboard"
      :company="originalCompany"
      :filter-company-id="effectiveFilterCompanyId"
    ></component>
  </div>
  <div v-else>Carregando...</div>
</template>

<script>
import { mapGetters } from "vuex";
import { getCompanyInfo, getCompanies } from "../../api/company";
import ComplaintDashboard from "./modules/complaint-dashboard";
import ConformityDashboard from "./modules/conformity-dashboard";
import DsarDashboard from "./modules/dsar-dashboard";
import OmbudsmanDashboard from "./modules/ombudsman-dashboard";
import Nr1Dashboard from "./modules/nr1-dashboard";
import GrcDashboard from "./modules/grc-dashboard";
import SearchableSelect from "@/components/SearchableSelect.vue";
import protocol from "../../mixins/protocol";

const GRC_MODULES = ["requirements", "risks", "controls", "actionPlans"];

export default {
  name: "CompanyDashboard",
  components: {
    ConformityDashboard,
    ComplaintDashboard,
    DsarDashboard,
    OmbudsmanDashboard,
    Nr1Dashboard,
    GrcDashboard,
    "nr1-dashboard": Nr1Dashboard,
    "grc-dashboard": GrcDashboard,
    SearchableSelect,
  },
  mixins: [protocol],
  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      maxHeight: "100%",
      loaded: false,
      company: {
        classification: {},
        modules: {},
      },
      originalCompany: null, // Empresa original do usuário (para manter os navs)
      activeModule: "complaint", // Valor default para garantir que um módulo seja mostrado
      protocol: null,
      companies: [],
      selectedCompanyId: null, // null = Todas as Empresas
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    isMasterUser() {
      return (
        this.profile.role === "root" || this.profile.role === "company-root"
      );
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    // Verifica se estamos no dashboard de uma empresa específica (não a do usuário logado)
    isViewingSpecificCompany() {
      // Se o ID da rota for diferente da empresa do usuário, estamos vendo uma empresa específica
      const routeId = parseInt(this.id);
      const userCompanyId = parseInt(this.profile.company);
      return routeId !== userCompanyId;
    },
    // Mostrar o filtro de empresas apenas se for company-root gestora E não estiver vendo uma empresa específica
    showCompanyFilter() {
      return this.isCompanyManager && !this.isViewingSpecificCompany;
    },
    // ID efetivo para filtrar os dashboards
    effectiveFilterCompanyId() {
      // Se estamos vendo uma empresa específica, usar sempre o ID dessa empresa
      if (this.isViewingSpecificCompany) {
        return parseInt(this.id);
      }
      // Caso contrário, usar o selectedCompanyId (null = Todas, ou ID selecionado)
      return this.selectedCompanyId;
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];
      this.companies.forEach((company) => {
        options.push({
          value: company._id,
          text: company.fantasyName || company.name,
        });
      });
      return options;
    },
    enabledModulesCount() {
      return this.getEnabledDashboardModules(
        this.originalCompany || this.company
      ).length;
    },
    filteredCompany() {
      // Retorna a empresa selecionada para filtrar ou null para "Todas"
      if (this.selectedCompanyId === null) {
        return { _id: null }; // Todas as empresas
      }
      return this.company;
    },
    singleModuleDashboard() {
      const availableModules = this.getEnabledDashboardModules(
        this.originalCompany || this.company
      );
      return availableModules.length > 0
        ? `${availableModules[0]}-dashboard`
        : undefined;
    },
  },
  watch: {
    // O filtro agora só passa o ID para os dashboards filtrarem
    // Não precisa recarregar a empresa
  },
  async created() {
    // Carregar empresas se for company-root gestora
    if (this.isCompanyManager) {
      await this.fetchCompanies();
    }

    const response = await getCompanyInfo(this.id);
    this.company = response.data;
    this.originalCompany = { ...response.data }; // Guardar empresa original
    this._id = this.id;
    this.company._id = this.id;
    this.loaded = true;
  },
  mounted() {
    // Define o primeiro módulo ativo após o componente estar completamente montado
    // Isso garante que as computed properties do Vuex (como permissions) estejam reativas
    this.setInitialActiveModule();
  },
  methods: {
    dashboardKeyForModule(module) {
      return GRC_MODULES.includes(module) ? "grc" : module;
    },
    resolvePermissionKey(module) {
      return module === "nr1" ? "questionnaire" : module;
    },
    hasPermission(module) {
      // Usar originalCompany para verificar permissões (manter navs fixos)
      const companyToCheck = this.originalCompany || this.company;
      if (module === "grc") {
        return GRC_MODULES.some((grcModule) =>
          this.hasPermissionForModule(grcModule, companyToCheck)
        );
      }
      if (!companyToCheck.modules || !companyToCheck.modules[module]?.enabled) {
        return false;
      }
      if (parseInt(this.profile.role) === this.profile.role) {
        const permissionKey = this.resolvePermissionKey(module);
        return (
          this.permissions[permissionKey] &&
          this.permissions[permissionKey].read
        );
      } else {
        return true;
      }
    },
    hasPermissionForModule(module, companyToCheck) {
      if (module === "grc") {
        return GRC_MODULES.some((grcModule) =>
          this.hasPermissionForModule(grcModule, companyToCheck)
        );
      }
      if (!companyToCheck.modules || !companyToCheck.modules[module]?.enabled) {
        return false;
      }
      if (parseInt(this.profile.role) === this.profile.role) {
        const permissionKey = this.resolvePermissionKey(module);
        return (
          this.permissions[permissionKey] &&
          this.permissions[permissionKey].read
        );
      } else {
        return true;
      }
    },
    getEnabledDashboardModules(companyToCheck) {
      const modules = Object.keys(companyToCheck.modules || {});
      const dashboards = [];

      for (const module of modules) {
        if (!companyToCheck.modules[module]?.enabled) {
          continue;
        }

        if (!this.hasPermissionForModule(module, companyToCheck)) {
          continue;
        }

        const dashboardKey = this.dashboardKeyForModule(module);
        if (!dashboards.includes(dashboardKey)) {
          dashboards.push(dashboardKey);
        }
      }

      return dashboards;
    },
    setActiveModule(module) {
      this.activeModule = module;
    },
    setInitialActiveModule() {
      const availableModules = this.getEnabledDashboardModules(
        this.originalCompany || this.company
      );
      if (availableModules.length > 0) {
        this.activeModule = availableModules[0];
      }
    },
    askAI() {
      console.log("Ask AI clicked");
    },
    async tryOpenProtocol() {
      if (!this.protocol) return;
      await this.openProtocol(this.protocol);
      this.protocol = null;
    },
    async fetchCompanies() {
      try {
        // Company-root gestora: a API retorna a própria empresa + empresas gerenciadas
        const response = await getCompanies();
        this.companies = Array.isArray(response.data)
          ? response.data
          : response.data.docs || [];
        // Manter selectedCompanyId como null (Todas as Empresas)
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    },
  },
};
</script>

<style scoped>
.module-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0;
}

.nav-buttons-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  max-width: 1400px;
  width: 100%;
}

/* Campo de busca de protocolo */
.protocol-search-wrapper {
  text-align: center;
  padding: 0;
  flex-shrink: 0;
  min-width: 380px; /* aumento da largura mínima */
  max-width: 500px; /* limite para telas grandes */
  flex: 1; /* permite expandir ocupando espaço disponível */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* Filtro de empresas */
.company-filter-wrapper {
  min-width: 250px;
  max-width: 300px;
  flex-shrink: 0;
  margin-right: 2rem; /* Espaçamento entre o select e os botões */
}

.company-filter-select {
  width: 100%;
  height: 48px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  color: #2d3748;
  background-color: white;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
}

.company-filter-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.search-container {
  position: relative;
  width: 100%;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
  height: 48px; /* altura maior */
  width: 85%;
}

.search-input {
  flex: 1;
  border: none;
  padding: 0.75rem 1.1rem; /* mais altura e espaço horizontal */
  font-size: 1rem; /* texto maior */
  color: #2d3748;
  background-color: transparent;
  outline: none;
}

.search-input::placeholder {
  color: #4a5568;
}

.search-button {
  background-color: transparent;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  margin-right: 0;
}

.search-button:hover {
  opacity: 0.7;
}

.search-button i {
  color: #000000;
  font-size: 0.9rem;
}

.buttons-group {
  display: flex;
  gap: 0.75rem;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
}

.nav-button {
  background: white;
  border: 2px solid #e9ecef;
  color: #6c757d;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  text-align: center;
  outline: none;
  position: relative;
  overflow: hidden;
}

.nav-button:hover {
  border-color: #007bff;
  color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.nav-button.active {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-color: #007bff;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transform: translateY(-2px);
}

.nav-button.active:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.nav-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.module-content {
  padding: 0 1rem;
}

/* Responsividade */
@media (max-width: 992px) {
  .nav-buttons-container {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }

  .protocol-search-wrapper {
    width: 100%;
    min-width: auto;
    max-width: 100%;
  }

  .buttons-group {
    justify-content: center;
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  /* ajuste responsivo campo protocolo */
  .protocol-search-wrapper {
    min-width: auto;
  }

  .nav-buttons-container {
    gap: 1.5rem;
    padding: 0.75rem 1rem;
  }

  .buttons-group {
    gap: 0.5rem;
  }

  .nav-button {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    min-width: 120px;
  }

  .module-content {
    padding: 0 0.5rem;
  }
}

@media (max-width: 576px) {
  .protocol-search-wrapper {
    width: 100%;
  }

  .nav-buttons-container {
    gap: 1rem;
  }

  .buttons-group {
    flex-direction: column;
    gap: 0.75rem;
  }

  .nav-button {
    width: 100%;
    min-width: auto;
  }

  .module-content {
    padding: 0 0.25rem;
  }
}

/* Estilos do botão Ask AI */
.ask-ai-button {
  background-color: black;
  border: 1px solid #cbcbcb;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 100px;
  height: 100%;
}

.ask-ai-button:hover {
  background-color: #212529;
  border-color: #cbcbcb;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.ask-ai-button i {
  color: white;
}

/* Responsividade para o botão Ask AI */
@media (max-width: 768px) {
  .ask-ai-button {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    min-width: 90px;
  }
}

@media (max-width: 576px) {
  .ask-ai-button {
    width: 100%;
    min-width: auto;
  }
}
</style>
